/**
 * Biomechanics utilities for the Shot Analyzer.
 *
 * Everything here operates on BlazePose's 33-landmark output. Each
 * landmark is {x, y, z, visibility} with x/y in *normalized image
 * coordinates* (0..1). Z is roughly normalized depth, smaller = closer
 * to camera. We never trust z for distance — we use it only for
 * left/right disambiguation when needed.
 *
 * The functions are pure: they accept a frame (or a series of frames)
 * and return numbers. Detection + thresholds live in `analyzeShot`.
 */

// BlazePose landmark indices (full body 33-point model).
export const L = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_HEEL: 29,     R_HEEL: 30,
};

// Angle at vertex B formed by A-B-C. Returns degrees [0, 180].
export function angleAt(A, B, C) {
  if (!A || !B || !C) return null;
  const v1x = A.x - B.x, v1y = A.y - B.y;
  const v2x = C.x - B.x, v2y = C.y - B.y;
  const dot = v1x * v2x + v1y * v2y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (!m1 || !m2) return null;
  const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

// Visibility filter — BlazePose flags occluded joints with low scores.
export const VIS_OK = (p) => p && (p.visibility ?? 1) >= 0.35;

/**
 * Pick the shooting side dynamically. We assume the side whose wrist
 * travels the highest during the recorded window is the shooting hand
 * (the elbow/shoulder of that side become the "shooting arm").
 */
export function pickShootingSide(frames) {
  let bestL = 1, bestR = 1; // lower y = higher on screen
  for (const f of frames) {
    if (VIS_OK(f[L.L_WRIST])) bestL = Math.min(bestL, f[L.L_WRIST].y);
    if (VIS_OK(f[L.R_WRIST])) bestR = Math.min(bestR, f[L.R_WRIST].y);
  }
  return bestR < bestL ? 'right' : 'left';
}

// Resolve {shoulder, elbow, wrist} indices for the chosen side.
export function armIndices(side) {
  return side === 'right'
    ? { shoulder: L.R_SHOULDER, elbow: L.R_ELBOW, wrist: L.R_WRIST, hip: L.R_HIP, knee: L.R_KNEE, ankle: L.R_ANKLE }
    : { shoulder: L.L_SHOULDER, elbow: L.L_ELBOW, wrist: L.L_WRIST, hip: L.L_HIP, knee: L.L_KNEE, ankle: L.L_ANKLE };
}

/**
 * Find the release frame within the captured window. Heuristic:
 *   - It's the local minimum y of the shooting wrist (highest on
 *     screen), AND the elbow angle is near-extended (>150°).
 * Returns the index in `frames` or -1 if undetermined.
 */
export function findReleaseFrame(frames, side) {
  const a = armIndices(side);
  let best = -1, bestY = 1;
  for (let i = 0; i < frames.length; i += 1) {
    const f = frames[i];
    if (!VIS_OK(f[a.wrist]) || !VIS_OK(f[a.elbow]) || !VIS_OK(f[a.shoulder])) continue;
    const elb = angleAt(f[a.shoulder], f[a.elbow], f[a.wrist]);
    if (elb == null || elb < 150) continue;
    if (f[a.wrist].y < bestY) {
      bestY = f[a.wrist].y;
      best = i;
    }
  }
  return best;
}

/**
 * Find the dip frame — the deepest knee bend before the release.
 */
export function findDipFrame(frames, side, releaseIdx) {
  const a = armIndices(side);
  let best = -1, minKnee = 180;
  const upper = releaseIdx > 0 ? releaseIdx : frames.length;
  for (let i = 0; i < upper; i += 1) {
    const f = frames[i];
    if (!VIS_OK(f[a.hip]) || !VIS_OK(f[a.knee]) || !VIS_OK(f[a.ankle])) continue;
    const knee = angleAt(f[a.hip], f[a.knee], f[a.ankle]);
    if (knee == null) continue;
    if (knee < minKnee) {
      minKnee = knee;
      best = i;
    }
  }
  return { idx: best, kneeAngle: minKnee };
}

// Lower-is-flatter: average lateral movement of the hips across frames.
// Used as a balance proxy.
function lateralStability(frames) {
  let count = 0, mean = 0, m2 = 0; // Welford
  for (const f of frames) {
    if (!VIS_OK(f[L.L_HIP]) || !VIS_OK(f[L.R_HIP])) continue;
    const cx = (f[L.L_HIP].x + f[L.R_HIP].x) / 2;
    count += 1;
    const delta = cx - mean;
    mean += delta / count;
    m2 += delta * (cx - mean);
  }
  if (count < 5) return null;
  const std = Math.sqrt(m2 / count);
  // Normalize: std ~0.005 is rock-solid (~100), std ~0.05 is sloppy (~0).
  return Math.max(0, Math.min(100, 100 * (1 - std / 0.05)));
}

// Forward/backward lean: angle of the spine vs vertical at release.
function spineLeanDeg(frame) {
  if (!frame) return null;
  if (!VIS_OK(frame[L.L_SHOULDER]) || !VIS_OK(frame[L.R_SHOULDER]) ||
      !VIS_OK(frame[L.L_HIP]) || !VIS_OK(frame[L.R_HIP])) return null;
  const shoulderMid = {
    x: (frame[L.L_SHOULDER].x + frame[L.R_SHOULDER].x) / 2,
    y: (frame[L.L_SHOULDER].y + frame[L.R_SHOULDER].y) / 2,
  };
  const hipMid = {
    x: (frame[L.L_HIP].x + frame[L.R_HIP].x) / 2,
    y: (frame[L.L_HIP].y + frame[L.R_HIP].y) / 2,
  };
  // 0° = perfectly vertical. Positive = leaning forward (shoulders ahead of hips).
  return Math.atan2(shoulderMid.x - hipMid.x, hipMid.y - shoulderMid.y) * (180 / Math.PI);
}

// Shoulder alignment vs hip line (square stance score 0..100).
function shoulderAlignmentScore(frame) {
  if (!frame) return null;
  if (!VIS_OK(frame[L.L_SHOULDER]) || !VIS_OK(frame[L.R_SHOULDER]) ||
      !VIS_OK(frame[L.L_HIP]) || !VIS_OK(frame[L.R_HIP])) return null;
  const shoulderTilt = Math.abs(frame[L.L_SHOULDER].y - frame[L.R_SHOULDER].y);
  const hipTilt = Math.abs(frame[L.L_HIP].y - frame[L.R_HIP].y);
  // Lower combined tilt = more square. 0.02 = good, 0.10 = bad.
  const tilt = (shoulderTilt + hipTilt) / 2;
  return Math.max(0, Math.min(100, 100 * (1 - tilt / 0.10)));
}

// Jerk-based rhythm score: smoother wrist motion = higher score.
function rhythmScore(frames, side) {
  const a = armIndices(side);
  const ys = [];
  for (const f of frames) {
    if (VIS_OK(f[a.wrist])) ys.push(f[a.wrist].y);
  }
  if (ys.length < 8) return null;
  let jerk = 0;
  for (let i = 2; i < ys.length - 1; i += 1) {
    const d = Math.abs((ys[i + 1] - 3 * ys[i] + 3 * ys[i - 1] - ys[i - 2]) || 0);
    jerk += d;
  }
  const mean = jerk / (ys.length - 3);
  // Empirical: mean ~0.003 = smooth, ~0.02 = jagged.
  return Math.max(0, Math.min(100, 100 * (1 - mean / 0.02)));
}

/**
 * Master analyzer. Takes the recorded frames + per-frame timestamps,
 * returns the complete `{ overall_score, metrics, errors }` payload.
 */
export function analyzeShot(frames, timestamps) {
  if (!frames || frames.length < 10) {
    return { ok: false, error: 'not_enough_frames' };
  }

  const side = pickShootingSide(frames);
  const arm = armIndices(side);
  const releaseIdx = findReleaseFrame(frames, side);
  const dip = findDipFrame(frames, side, releaseIdx);

  const releaseFrame = releaseIdx >= 0 ? frames[releaseIdx] : null;
  const dipFrame = dip.idx >= 0 ? frames[dip.idx] : null;

  // Core metrics ---------------------------------------------------------
  const elbowAngleRelease = releaseFrame
    ? angleAt(releaseFrame[arm.shoulder], releaseFrame[arm.elbow], releaseFrame[arm.wrist])
    : null;

  const kneeBendMin = dip.idx >= 0 ? dip.kneeAngle : null;

  const releaseHeightNorm = releaseFrame
    ? 1 - releaseFrame[arm.wrist].y // higher on screen = bigger number (intuitive)
    : null;

  const dipToReleaseMs =
    dip.idx >= 0 && releaseIdx > dip.idx
      ? (timestamps[releaseIdx] - timestamps[dip.idx])
      : null;

  // Follow-through: time wrist stays high after release (≤0.05 below peak)
  let followThroughMs = null;
  if (releaseIdx >= 0) {
    const peakY = frames[releaseIdx][arm.wrist].y;
    let lastIdx = releaseIdx;
    for (let i = releaseIdx + 1; i < frames.length; i += 1) {
      if (!VIS_OK(frames[i][arm.wrist])) break;
      if (frames[i][arm.wrist].y - peakY > 0.05) break;
      lastIdx = i;
    }
    followThroughMs = timestamps[lastIdx] - timestamps[releaseIdx];
  }

  const balance = lateralStability(frames);
  const align = shoulderAlignmentScore(releaseFrame || frames[Math.floor(frames.length / 2)]);
  const rhythm = rhythmScore(frames, side);
  const lean = spineLeanDeg(releaseFrame);

  // Leg drive: combination of deep dip + crisp dip→release timing.
  let legDrive = null;
  if (kneeBendMin != null && dipToReleaseMs != null) {
    const dipScore = Math.max(0, Math.min(100, (180 - kneeBendMin) * 2.5)); // 140°→100, 180°→0
    const timingScore = Math.max(0, Math.min(100, 100 - Math.abs(dipToReleaseMs - 350) / 6));
    legDrive = (dipScore + timingScore) / 2;
  }

  // Error detection rules -----------------------------------------------
  const errors = [];
  if (elbowAngleRelease != null && elbowAngleRelease < 80) errors.push('open_elbow');
  if (releaseHeightNorm != null && releaseHeightNorm < 0.55) errors.push('low_release');
  if (kneeBendMin != null && kneeBendMin > 165) errors.push('no_legs');
  if (lean != null && lean > 12) errors.push('lean_forward');
  if (lean != null && lean < -10) errors.push('lean_backward');
  if (rhythm != null && rhythm < 45) errors.push('broken_rhythm');
  if (followThroughMs != null && followThroughMs < 120) errors.push('lack_of_fluidity');
  if (align != null && align < 50) errors.push('misalignment');

  // Overall: weighted average of available sub-scores.
  const subs = [
    elbowAngleRelease != null ? Math.max(0, 100 - Math.abs(elbowAngleRelease - 90) * 2) : null,
    balance,
    align,
    rhythm,
    legDrive,
    followThroughMs != null ? Math.max(0, Math.min(100, followThroughMs / 4)) : null,
  ].filter((x) => x != null);
  const overall = subs.length ? Math.round(subs.reduce((a, b) => a + b, 0) / subs.length) : 0;

  return {
    ok: true,
    side,
    overall_score: overall,
    release_keypoints: releaseFrame
      ? releaseFrame.map((p) => ({ x: +p.x.toFixed(3), y: +p.y.toFixed(3), v: +(p.visibility ?? 1).toFixed(2) }))
      : null,
    metrics: {
      elbow_angle_release: elbowAngleRelease != null ? +elbowAngleRelease.toFixed(1) : null,
      knee_bend_min: kneeBendMin != null ? +kneeBendMin.toFixed(1) : null,
      shoulder_alignment: align != null ? +align.toFixed(1) : null,
      balance_score: balance != null ? +balance.toFixed(1) : null,
      follow_through_ms: followThroughMs != null ? +followThroughMs.toFixed(0) : null,
      rhythm_score: rhythm != null ? +rhythm.toFixed(1) : null,
      release_height_norm: releaseHeightNorm != null ? +releaseHeightNorm.toFixed(3) : null,
      dip_to_release_ms: dipToReleaseMs != null ? +dipToReleaseMs.toFixed(0) : null,
      leg_drive_score: legDrive != null ? +legDrive.toFixed(1) : null,
    },
    errors,
  };
}

/**
 * Silhouette match check. Compares live pose vs a reference set-shot
 * pose (also expressed as 33 landmarks). We use a translation+scale
 * invariant comparison: align hips to (0, 0) and scale by torso length.
 */
export function silhouetteMatch(live, ref) {
  if (!live || !ref) return 0;
  // Pick visible anchor points; bail if not enough.
  const need = [L.L_HIP, L.R_HIP, L.L_SHOULDER, L.R_SHOULDER, L.L_ELBOW, L.R_ELBOW, L.L_KNEE, L.R_KNEE];
  for (const i of need) {
    if (!VIS_OK(live[i])) return 0;
  }
  const liveHip = { x: (live[L.L_HIP].x + live[L.R_HIP].x) / 2, y: (live[L.L_HIP].y + live[L.R_HIP].y) / 2 };
  const refHip = { x: (ref[L.L_HIP].x + ref[L.R_HIP].x) / 2, y: (ref[L.L_HIP].y + ref[L.R_HIP].y) / 2 };
  const liveShoulder = { x: (live[L.L_SHOULDER].x + live[L.R_SHOULDER].x) / 2, y: (live[L.L_SHOULDER].y + live[L.R_SHOULDER].y) / 2 };
  const refShoulder = { x: (ref[L.L_SHOULDER].x + ref[L.R_SHOULDER].x) / 2, y: (ref[L.L_SHOULDER].y + ref[L.R_SHOULDER].y) / 2 };
  const liveScale = Math.hypot(liveShoulder.x - liveHip.x, liveShoulder.y - liveHip.y) || 1;
  const refScale = Math.hypot(refShoulder.x - refHip.x, refShoulder.y - refHip.y) || 1;
  let acc = 0, n = 0;
  for (const i of need) {
    const lx = (live[i].x - liveHip.x) / liveScale;
    const ly = (live[i].y - liveHip.y) / liveScale;
    const rx = (ref[i].x - refHip.x) / refScale;
    const ry = (ref[i].y - refHip.y) / refScale;
    const d = Math.hypot(lx - rx, ly - ry);
    acc += d;
    n += 1;
  }
  const meanD = acc / n;
  // 0 = perfect overlap, ~0.6 = no match. Map to 0..100.
  return Math.max(0, Math.min(100, 100 * (1 - meanD / 0.5)));
}

/**
 * Reference set-shot pose (right-handed, facing camera, mid-dip).
 * Built once empirically — the user only needs to look like this.
 */
export const REFERENCE_SET_SHOT = [
  /* 0 NOSE        */ { x: 0.50, y: 0.20 },
  /* 1 LEYE_INNER  */ { x: 0.48, y: 0.19 },
  /* 2 LEYE        */ { x: 0.47, y: 0.19 },
  /* 3 LEYE_OUTER  */ { x: 0.46, y: 0.19 },
  /* 4 REYE_INNER  */ { x: 0.52, y: 0.19 },
  /* 5 REYE        */ { x: 0.53, y: 0.19 },
  /* 6 REYE_OUTER  */ { x: 0.54, y: 0.19 },
  /* 7 LEAR        */ { x: 0.45, y: 0.21 },
  /* 8 REAR        */ { x: 0.55, y: 0.21 },
  /* 9 MOUTH_L     */ { x: 0.49, y: 0.22 },
  /* 10 MOUTH_R    */ { x: 0.51, y: 0.22 },
  /* 11 L_SHOULDER */ { x: 0.42, y: 0.32 },
  /* 12 R_SHOULDER */ { x: 0.58, y: 0.32 },
  /* 13 L_ELBOW    */ { x: 0.40, y: 0.46 },
  /* 14 R_ELBOW    */ { x: 0.60, y: 0.46 },
  /* 15 L_WRIST    */ { x: 0.45, y: 0.34 },
  /* 16 R_WRIST    */ { x: 0.56, y: 0.34 },
  /* 17 L_PINKY    */ { x: 0.44, y: 0.33 },
  /* 18 R_PINKY    */ { x: 0.57, y: 0.33 },
  /* 19 L_INDEX    */ { x: 0.46, y: 0.32 },
  /* 20 R_INDEX    */ { x: 0.55, y: 0.32 },
  /* 21 L_THUMB    */ { x: 0.45, y: 0.33 },
  /* 22 R_THUMB    */ { x: 0.56, y: 0.33 },
  /* 23 L_HIP      */ { x: 0.45, y: 0.55 },
  /* 24 R_HIP      */ { x: 0.55, y: 0.55 },
  /* 25 L_KNEE     */ { x: 0.44, y: 0.72 },
  /* 26 R_KNEE     */ { x: 0.56, y: 0.72 },
  /* 27 L_ANKLE    */ { x: 0.44, y: 0.92 },
  /* 28 R_ANKLE    */ { x: 0.56, y: 0.92 },
  /* 29 L_HEEL     */ { x: 0.43, y: 0.94 },
  /* 30 R_HEEL     */ { x: 0.57, y: 0.94 },
  /* 31 L_FOOT     */ { x: 0.46, y: 0.95 },
  /* 32 R_FOOT     */ { x: 0.54, y: 0.95 },
].map((p) => ({ ...p, z: 0, visibility: 1 }));
